import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { SvgImportData } from "@routes/IconeSvg";
import "@css/pages/employer/listeEmployer.css";
import BtnActions from "@components/buttons/ButtonsModals";
import ButtonCloseModal from "@components/tailwindComponents/CloseModal";
import ListboxEmploye from "@components/tailwindComponents/ListboxEmploye";
import ListboxHierarchical from "@components/tailwindComponents/ListboxHierarchical";
import ListboxGroupe from "@components/tailwindComponents/ListboxGroupe/index";
import ListboxSimple from "@components/tailwindComponents/ListboxSimple";
import useBulkInjectionFields from "./hooks/useBulkInjectionFields";
import { applyBulkInjection } from "./utils/bulkInjectionHelpers";

const EMPTY_IMPORT_DATA = {};

export default function BulkEditDataInjection({
  isOpen,
  onClose,
  selectedItemNumber,
  isSectionDisplayed,
  hiddenColumns = {},
  title,
  maxAdvantageTypes = 0,
  isSaving = false,
  hasBankingAccess = false,
  onInjectableFieldsChange
}) {
  const { t } = useTranslation([
    "pages.employés.importation",
    "components.liSimple"
  ]);
  const methods = useFormContext();
  const [dataForChange, setDataForChange] = useState(EMPTY_IMPORT_DATA);
  const [isMounted, setIsMounted] = useState(isOpen);
  const collapseRef = useRef(null);
  const visibleFields = useBulkInjectionFields({
    isOpen,
    employees: methods.getValues("employees") || [],
    isSectionDisplayed,
    hiddenColumns,
    maxAdvantageTypes,
    isSaving,
    hasBankingAccess
  });
  const visibleFieldsKey = visibleFields.map((field) => field.id).join("|");
  const hasInjectableFields = visibleFields.some(
    (field) => !field.disabled && field.options.length > 0
  );

  useEffect(() => {
    onInjectableFieldsChange?.(hasInjectableFields);
  }, [hasInjectableFields, onInjectableFieldsChange]);

  useEffect(() => {
    setDataForChange(EMPTY_IMPORT_DATA);
  }, [isOpen, visibleFieldsKey]);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      return;
    }
    const timer = setTimeout(() => setIsMounted(false), 300);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        collapseRef.current &&
        !collapseRef.current.contains(event.target) &&
        !event.target.closest("[data-import-data-button]")
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const handleDataForChange = (value, field) => {
    setDataForChange((previousData) => ({
      ...previousData,
      [field]: value
    }));
  };

  const handleApplyImportData = () => {
    if (isSaving) return;
    applyBulkInjection({
      employees: methods.getValues("employees") || [],
      fields: visibleFields,
      values: dataForChange,
      setValue: methods.setValue
    });

    onClose();
  };

  if (!isMounted) return null;

  return (
    <div
      ref={collapseRef}
      className={`${isOpen ? "side-collapse side-collapse--expanded" : "side-collapse arh-pointer-events-none"} !arh-z-30`}
    >
      <div className="arh-flex arh-h-full arh-w-full arh-flex-col arh-overflow-y-auto arh-px-14 arh-pb-8">
        <div className="arh-sticky arh-top-0 arh-z-10 arh-flex arh-items-center arh-justify-between arh-bg-white arh-py-10">
          <div className="arh-flex arh-items-center arh-gap-3 arh-leading-none">
            <span
              className="arh-flex arh-items-center arh-justify-center"
              style={{ width: 18, height: 18 }}
            >
              <SvgImportData color="#000000" />
            </span>
            <h3 className="arh-m-0 arh-flex arh-items-center arh-text-left arh-font-poppins arh-text-lg arh-font-semibold arh-text-black">
              {title}
            </h3>
          </div>
          <ButtonCloseModal onClick={onClose} customClass="!arh-static" />
        </div>

        <div className="arh-mt-5 arh-flex arh-flex-col">
          {visibleFields.map((field, index) => {
            const Select =
              field.component === "employee"
                ? ListboxEmploye
                : field.component === "hierarchical"
                  ? ListboxHierarchical
                : field.component === "group"
                  ? ListboxGroupe
                  : ListboxSimple;
            return (
              <div
                key={field.id}
                className={index ? " blackSvgColor mt-3" : " blackSvgColor"}
              >
                <label
                  className={`arh-font-poppins arh-text-xs arh-font-medium ${
                    field.disabled
                      ? "arh-text-greyDisabledText"
                      : "arh-text-black"
                  }`}
                >
                  {field.label}
                </label>
                <Select
                  name={field.id}
                  placeholder={field.label}
                  optionList={field.options}
                  optionTextAttribut={field.textAttribute || "name"}
                  optionValueAttibut={field.valueAttribute || "_id"}
                  selectedValue={
                    dataForChange[field.id] || (field.multiple ? [] : null)
                  }
                  setSelectedValue={(value) =>
                    handleDataForChange(value, field.id)
                  }
                  setLatestSelectedHierarchical={() => {}}
                  multiple={field.multiple}
                  customTranslator={field.translator}
                  readOnly={field.disabled}
                  withTooltip={field.withTooltip}
                  classLabel={
                    field.component === "group" ? "arh-flex" : undefined
                  }
                  withSearch
                  noRadio
                  showReset
                />
              </div>
            );
          })}
          <div>
            <BtnActions
              title1={t("Annuler")}
              events1={() => {
                setDataForChange(EMPTY_IMPORT_DATA);
                onClose();
              }}
              title2={t("Appliquer")}
              events2={handleApplyImportData}
              colorTheme="green"
              disabled={
                isSaving ||
                !selectedItemNumber ||
                !visibleFields.some((field) => !field.disabled)
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
